/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import Swal from "sweetalert2";
import pharmacySoftwareService from "../../services/pharmacy-software.service";
import EditPharmacySoftwareModal from "../EditPharmacySoftwareModal";
import "./index.scss";

const SEND_MODE_LABELS = {
  on_hold: "Add On Hold",
  intake: "Intake",
};

/**
 * `isOwnBranchOnly` is set for a pharmacy administrator, who can only ever have one connection
 * for their own branch. The API already confines them to it; this just avoids offering an "add"
 * button that would fail on the unique branch/software constraint.
 */
const PharmacySoftwareDataGrid = ({ isOwnBranchOnly = false }) => {
  const [records, setRecords] = useState([]);
  const [record, setRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [editModalShow, setEditModalShow] = useState(false);
  const [testingId, setTestingId] = useState(null);

  const loadList = useCallback(async () => {
    setIsLoading(true);
    const result = await pharmacySoftwareService.ListConnections();
    setIsLoading(false);
    if (result.error) return;
    setRecords(result.data || []);
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const handleTest = async (row) => {
    setTestingId(row.Id);
    const result = await pharmacySoftwareService.TestConnection(row.Id);
    setTestingId(null);

    if (result.error) {
      Swal.fire({
        icon: "error",
        title: "Could not run the test",
        text: result.message || "Please try again.",
      });
      return;
    }

    const outcome = result.data;
    Swal.fire({
      icon: outcome?.status === "success" ? "success" : "error",
      title:
        outcome?.status === "success"
          ? "Connection is working"
          : "Connection failed",
      text: outcome?.message,
    });
    await loadList();
  };

  const handleDelete = async (row) => {
    const confirm = await Swal.fire({
      title: "Remove this connection?",
      text: `${row.PharmacyBranchName || "This pharmacy"} will no longer be able to send prescriptions to ${row.SoftwareLabel}.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Remove",
    });
    if (!confirm.isConfirmed) return;

    const result = await pharmacySoftwareService.DeleteConnection(row.Id);
    if (!result.error) {
      await loadList();
    }
  };

  const renderTestStatus = (row) => {
    if (!row.LastTestStatus) {
      return <span className="text-muted">Never tested</span>;
    }
    return (
      <span
        className={`badge bg-${row.LastTestStatus === "success" ? "success" : "danger"}`}
        title={row.LastTestMessage || ""}
      >
        {row.LastTestStatus === "success" ? "Passed" : "Failed"}
        {row.LastTestedOn ? ` · ${moment(row.LastTestedOn).format("MM/DD/YY HH:mm")}` : ""}
      </span>
    );
  };

  return (
    <div className="pharmacy-software-datagrid">
      <div className="card h-100">
        <div className="card-header d-flex justify-content-between align-items-center bg-white flex-wrap gap-2">
          {isOwnBranchOnly && records.length > 0 ? (
            <span className="fw-semibold">Your pharmacy software</span>
          ) : (
            <button
              className="btn btn-default"
              type="button"
              onClick={() => {
                setRecord(null);
                setEditModalShow(true);
              }}
            >
              <i className="fa fa-plus-circle me-1"></i>
              {isOwnBranchOnly ? "Connect my pharmacy" : "Connect a pharmacy"}
            </button>
          )}
          <small className="text-muted">
            Credentials are stored encrypted and are never shown again after saving.
          </small>
        </div>

        <div className="card-body datagrid p-0">
          <table className="table mb-0">
            <thead>
              <tr>
                <th className="text-center">#</th>
                <th>Pharmacy</th>
                <th>Software</th>
                <th>Host</th>
                <th className="text-center">Employee ID</th>
                <th className="text-center">Default mode</th>
                <th className="text-center">Status</th>
                <th className="text-center">Last test</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="text-center text-muted py-4">
                    Loading connections…
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center text-muted py-4">
                    {isOwnBranchOnly
                      ? "Your pharmacy is not connected to an external software yet."
                      : "No pharmacy is connected to an external software yet."}
                  </td>
                </tr>
              ) : (
                records.map((row) => (
                  <tr key={row.Id}>
                    <td className="text-center">{row.Id}</td>
                    <td>
                      <div className="fw-semibold">{row.PharmacyBranchName || "—"}</div>
                      {row.NCPDP ? (
                        <small className="text-muted">NCPDP {row.NCPDP}</small>
                      ) : null}
                    </td>
                    <td>{row.SoftwareLabel}</td>
                    <td>
                      <code>{row.Host}</code>
                      {!row.RejectUnauthorized ? (
                        <div>
                          <small className="text-warning-emphasis">
                            TLS verification off
                          </small>
                        </div>
                      ) : null}
                    </td>
                    <td className="text-center">
                      <code>{row.RequestedByEmployeeId}</code>
                    </td>
                    <td className="text-center">
                      {SEND_MODE_LABELS[row.DefaultSendMode] || row.DefaultSendMode}
                    </td>
                    <td className="text-center">
                      <span className={`badge bg-${row.IsActive ? "success" : "secondary"}`}>
                        {row.IsActive ? "Active" : "Disabled"}
                      </span>
                    </td>
                    <td className="text-center">{renderTestStatus(row)}</td>
                    <td>
                      <div className="d-flex justify-content-center">
                        <a
                          href="#"
                          className="view"
                          title="Test connection"
                          onClick={(e) => {
                            e.preventDefault();
                            handleTest(row);
                          }}
                        >
                          <i
                            className={
                              testingId === row.Id
                                ? "fa fa-spinner fa-spin"
                                : "fa fa-plug"
                            }
                          ></i>
                        </a>
                        <a
                          href="#"
                          className="edit"
                          title="Edit"
                          onClick={(e) => {
                            e.preventDefault();
                            setRecord(row);
                            setEditModalShow(true);
                          }}
                        >
                          <i className="fa fa-edit"></i>
                        </a>
                        <a
                          href="#"
                          className="delete"
                          title="Remove"
                          onClick={(e) => {
                            e.preventDefault();
                            handleDelete(row);
                          }}
                        >
                          <i className="fa fa-trash"></i>
                        </a>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <EditPharmacySoftwareModal
        show={editModalShow}
        record={record}
        isOwnBranchOnly={isOwnBranchOnly}
        onHide={async (refresh) => {
          setEditModalShow(false);
          setRecord(null);
          if (refresh) {
            await loadList();
          }
        }}
      />
    </div>
  );
};

export default PharmacySoftwareDataGrid;
